import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiCheckCircle, FiAlertTriangle, FiFileText, FiGlobe, FiAward, FiArrowRight, FiClock } = FiIcons;

const ComplianceManagementPage = () => {
  const regulations = [
    {
      name: 'GDPR',
      title: 'General Data Protection Regulation',
      description: 'Comprehensive EU data protection and privacy regulation',
      icon: FiGlobe,
      requirements: [
        'Data subject rights management',
        'Consent management and tracking',
        'Data breach notification procedures',
        'Privacy by design implementation',
        'Cross-border data transfer controls'
      ]
    },
    {
      name: 'HIPAA',
      title: 'Health Insurance Portability and Accountability Act',
      description: 'US healthcare data protection and privacy standards',
      icon: FiShield,
      requirements: [
        'Protected Health Information (PHI) safeguarding',
        'Access control and audit trails',
        'Business associate agreements',
        'Risk assessment and management',
        'Incident response procedures'
      ]
    },
    {
      name: 'CCPA/CPRA',
      title: 'California Consumer Privacy Act',
      description: 'California state privacy law granting consumer data rights',
      icon: FiFileText,
      requirements: [
        'Consumer rights fulfillment',
        'Data transparency and disclosure',
        'Opt-out mechanisms implementation',
        'Data inventory and mapping',
        'Vendor risk management'
      ]
    },
    {
      name: 'SOX',
      title: 'Sarbanes-Oxley Act',
      description: 'Financial reporting and internal controls requirements',
      icon: FiAward,
      requirements: [
        'Financial data integrity controls',
        'Audit trail maintenance',
        'Segregation of duties enforcement',
        'Document retention policies',
        'Internal controls testing'
      ]
    }
  ];

  const features = [
    { icon: FiCheckCircle, title: 'Automated Compliance Monitoring', description: 'Continuous monitoring of compliance status with real-time alerts and reporting' },
    { icon: FiFileText, title: 'Policy Management', description: 'Centralized policy creation, distribution, and acknowledgment tracking' },
    { icon: FiAlertTriangle, title: 'Risk Assessment', description: 'Automated risk identification, assessment, and mitigation tracking' },
    { icon: FiShield, title: 'Audit Readiness', description: 'Comprehensive audit preparation with evidence collection and reporting' }
  ];

  const benefits = [
    { metric: '95%', description: 'Reduction in compliance violations' },
    { metric: '70%', description: 'Faster audit preparation' },
    { metric: '85%', description: 'Improved risk visibility' },
    { metric: '60%', description: 'Lower compliance costs' }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-900 to-teal-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                Comprehensive Compliance Management
              </h1>
              <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
                Navigate complex regulatory requirements with confidence using our automated compliance management solutions powered by Microsoft Purview.
              </p>
              <Link
                to="/purview-audit"
                className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105"
              >
                Get Purview Compliance Assessment
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Compliance Excellence Delivered
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your compliance program from reactive to proactive with measurable results
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-green-600 mb-2">{benefit.metric}</div>
                <div className="text-gray-600">{benefit.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulations Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Major Regulations We Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive compliance coverage for major global and industry-specific regulations
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {regulations.map((regulation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-lg shadow-lg"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <SafeIcon icon={regulation.icon} className="h-8 w-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{regulation.name}</h3>
                    <p className="text-gray-600 mb-2">{regulation.title}</p>
                    <p className="text-gray-600">{regulation.description}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Key Requirements:</h4>
                  <ul className="space-y-2">
                    {regulation.requirements.map((requirement, idx) => (
                      <li key={idx} className="flex items-center">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Advanced Compliance Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful tools to automate and streamline your compliance management
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <SafeIcon icon={feature.icon} className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ensure Continuous Compliance
          </h2>
          <p className="text-xl mb-8 text-green-100">
            Let our experts help you build a robust compliance management system that adapts to changing regulations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/purview-audit"
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105"
            >
              Get Purview Compliance Assessment
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComplianceManagementPage;